import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Loader2, Plus, Home, CalendarDays } from "lucide-react";

/**
 * AdminAddPropertyBookingDialog — iter259 (2026-08-26)
 *
 * Owner directive: admin needs a ONE-STEP way to add a new property AND
 * book a service for an existing customer directly from the customer's
 * profile.  Steps 1-5 in a single modal:
 *
 *   STEP 1 — Property: label + address + sqft + yard area
 *   STEP 2 — Service: service_key + frequency
 *   STEP 3 — Price: computed via the existing pricing engine (admin
 *            can override)
 *   STEP 4 — Service date: date picker
 *   STEP 5 — Create: writes the property + booking + per-property
 *            recurring schedule (if cadence != one_time)
 *
 * Each property/service becomes a completely independent booking so the
 * Admin Calendar shows it as its own job on the selected date.
 */

const YARD = [
  { key: "front", label: "Front" },
  { key: "back", label: "Back" },
  { key: "both", label: "Full" },
];
const FREQ = [
  { key: "one_time", label: "One-time" },
  { key: "weekly", label: "Weekly" },
  { key: "biweekly", label: "Every 2 Weeks" },
  { key: "monthly", label: "Monthly" },
];
// Kept minimal — the standard JDSN services.  If a service isn't in the
// dropdown, admin can still use the customer-facing flow.
const SERVICES = [
  { key: "lawn_mowing", label: "Lawn Mowing" },
  { key: "hedge_trimming", label: "Hedge Trimming" },
  { key: "mulching", label: "Mulching" },
  { key: "leaf_removal", label: "Leaf Removal" },
  { key: "yard_cleanup", label: "Yard Cleanup" },
  { key: "aeration", label: "Aeration" },
];

const EMPTY = {
  label: "", address: "", square_feet: "", yard_area: "both", notes: "",
  service_key: "lawn_mowing", frequency: "biweekly",
  preferred_date: "", agreed_price: "", payment_method: "cash_on_arrival",
};

export default function AdminAddPropertyBookingDialog({
  userId, open, onOpenChange, onCreated,
}) {
  const [form, setForm] = useState(EMPTY);
  const [pricePreview, setPricePreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);

  useEffect(() => {
    if (!open) setForm(EMPTY);
    if (!open) setPricePreview(null);
  }, [open]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Live-preview the price via the public estimate endpoint whenever
  // sqft/frequency/service/yard changes.  Runs client-side against the
  // same pricing engine used at booking time.
  const canPreview = () => {
    const sqft = Number.parseInt(form.square_feet, 10);
    return Number.isFinite(sqft) && sqft > 0 && form.service_key
      && form.frequency && form.yard_area;
  };
  const runPreview = async () => {
    if (!canPreview()) { setPricePreview(null); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/estimates/calculate", {
        service_key: form.service_key,
        square_feet: Number.parseInt(form.square_feet, 10),
        mowable_sqft: Number.parseInt(form.square_feet, 10),
        frequency: form.frequency,
        yard_area: form.yard_area,
        preferred_date: form.preferred_date || "",
        preferred_time: "Morning (7am - 11am)",
      });
      setPricePreview(data);
    } catch {
      setPricePreview(null);
    } finally {
      setPreviewBusy(false);
    }
  };
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(runPreview, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, form.square_feet, form.service_key, form.frequency, form.yard_area]);

  const submit = async () => {
    if (!form.address?.trim()) return toast.error("Enter the property address");
    const sqft = Number.parseInt(form.square_feet, 10);
    if (!Number.isFinite(sqft) || sqft <= 0) return toast.error("Enter a valid lawn size (sq ft)");
    if (!form.preferred_date) return toast.error("Pick the service date");
    setSubmitBusy(true);
    try {
      const { data } = await api.post(
        `/admin/customers/${userId}/add-property-booking`,
        {
          property_label: form.label?.trim() || undefined,
          address: form.address.trim(),
          square_feet: sqft,
          yard_area: form.yard_area,
          notes: form.notes?.trim() || null,
          service_key: form.service_key,
          frequency: form.frequency,
          preferred_date: form.preferred_date,
          agreed_price: form.agreed_price ? Number(form.agreed_price) : null,
          payment_method: form.payment_method,
        },
      );
      toast.success(`Booking created — ${data?.property?.label || "property"} on ${form.preferred_date}`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitBusy(false);
    }
  };

  const displayPrice = pricePreview?.total ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
                     data-testid="admin-add-property-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#047857]" /> Add property &amp; book service
          </DialogTitle>
          <DialogDescription>
            Creates a new property under this customer&apos;s account plus a
            fully-priced booking on the date you pick.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* STEP 1 — Property */}
          <section className="rounded-2xl border border-gray-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500 flex items-center gap-1.5 mb-2">
              <Home className="w-3 h-3" /> Step 1 · Property
            </div>
            <div className="grid gap-2">
              <div>
                <Label>Name (optional)</Label>
                <Input value={form.label} onChange={(e) => set("label", e.target.value)}
                       placeholder="Home, Rental, Property #2…"
                       data-testid="admin-addbook-label" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => set("address", e.target.value)}
                       placeholder="123 Main St Jacksonville FL"
                       data-testid="admin-addbook-address" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Lawn size (sq ft)</Label>
                  <Input type="number" min="0" value={form.square_feet}
                         onChange={(e) => set("square_feet", e.target.value)}
                         placeholder="8500"
                         data-testid="admin-addbook-sqft" />
                </div>
                <div>
                  <Label>Yard area</Label>
                  <div className="mt-1 grid grid-cols-3 gap-1.5">
                    {YARD.map((o) => (
                      <button key={o.key} type="button"
                              onClick={() => set("yard_area", o.key)}
                              className={`rounded-lg border-2 py-2 text-[11px] font-extrabold ${
                                form.yard_area === o.key
                                  ? "border-[#047857] bg-emerald-50 text-[#047857]"
                                  : "border-gray-200 text-gray-600"
                              }`}
                              data-testid={`admin-addbook-yard-${o.key}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea rows={2} value={form.notes}
                          onChange={(e) => set("notes", e.target.value)}
                          placeholder="Gate on left, dog in yard, etc." />
              </div>
            </div>
          </section>

          {/* STEP 2 — Service */}
          <section className="rounded-2xl border border-gray-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500 mb-2">
              Step 2 · Service
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Service</Label>
                <select value={form.service_key}
                        onChange={(e) => set("service_key", e.target.value)}
                        className="mt-1 w-full h-10 px-2 rounded-lg border border-gray-300 text-sm font-semibold bg-white"
                        data-testid="admin-addbook-service">
                  {SERVICES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Frequency</Label>
                <select value={form.frequency}
                        onChange={(e) => set("frequency", e.target.value)}
                        className="mt-1 w-full h-10 px-2 rounded-lg border border-gray-300 text-sm font-semibold bg-white"
                        data-testid="admin-addbook-frequency">
                  {FREQ.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* STEP 3 — Price */}
          <section className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-800">
                Step 3 · Price
              </div>
              {previewBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-700" />}
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <div className="text-2xl font-extrabold text-emerald-900"
                   data-testid="admin-addbook-price-preview">
                {displayPrice != null ? `$${displayPrice.toFixed(2)}` : "—"}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Override</Label>
                <Input type="number" step="0.01" min="0"
                       value={form.agreed_price}
                       onChange={(e) => set("agreed_price", e.target.value)}
                       placeholder={displayPrice != null ? displayPrice.toFixed(2) : ""}
                       className="h-9 w-28 text-sm font-bold"
                       data-testid="admin-addbook-override" />
              </div>
            </div>
            <div className="mt-1 text-[10px] text-emerald-800/80">
              Computed by JDSN pricing engine — sqft × frequency × yard.
            </div>
          </section>

          {/* STEP 4 — Service date */}
          <section className="rounded-2xl border border-gray-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500 flex items-center gap-1.5 mb-2">
              <CalendarDays className="w-3 h-3" /> Step 4 · Service date
            </div>
            <input type="date"
                   min={new Date().toISOString().slice(0, 10)}
                   value={form.preferred_date}
                   onChange={(e) => set("preferred_date", e.target.value)}
                   className="w-full h-12 px-3 rounded-lg border-2 border-emerald-300 text-base font-bold text-gray-900 bg-white"
                   data-testid="admin-addbook-date" />
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full"
                  onClick={() => onOpenChange(false)}
                  data-testid="admin-addbook-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitBusy}
                  className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white"
                  data-testid="admin-addbook-submit">
            {submitBusy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            Create service
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
